/**
 * W-06 "Eldeki stok" matrix — the pure half of the screen: which columns the
 * matrix has, how the balance rows pivot onto them, what colour a number
 * carries, and how the two saved views sort.
 *
 * Everything here is a function of rows the views already returned
 * (`v_stock_item_summary`, `v_stock_matrix`, `warehouse_zones`, `v_warehouses`),
 * so the same logic serves the web matrix today and the mobile stock list
 * later, and `packages/core/test/stock-matrix.test.ts` pins it against the ten
 * rows of the artboard.
 *
 * Quantities arrive in base units and are shown in the item's stock unit; the
 * division happens once here (`matrixQty`) so no screen divides by a factor of
 * its own.
 */
import { D, type Dec, type DecimalInput } from '../money';
import { formatNumber, MINUS } from '../format';
import { ITEM_TYPE_ORDER, type ItemStatus, type ItemType, type WarehouseType } from '../constants';
import type { StockStatus } from './status';

// ── columns ─────────────────────────────────────────────────────────────────
/** `warehouse_zones` as the matrix reads it — the columns are zones, not warehouses. */
export interface MatrixZoneInput {
  id: string | null;
  warehouse_id: string | null;
  code?: string | null;
  short_label?: string | null;
  sort?: number | null;
  show_in_matrix?: boolean | null;
  is_active?: boolean | null;
  deleted_at?: string | null;
}

/** `v_warehouses` / `warehouses` — only what the column order and label need. */
export interface MatrixWarehouseInput {
  id: string | null;
  code?: string | null;
  short_label?: string | null;
  type?: WarehouseType | null;
  sort?: number | null;
  is_active?: boolean | null;
  deleted_at?: string | null;
}

/** One entry of `settings.stock_matrix.columns` (D3): the owner's order and visibility. */
export interface MatrixColumnSetting {
  id?: string | null;
  visible?: boolean | null;
}

export interface StockMatrixColumn {
  zoneId: string;
  warehouseId: string;
  /** Header text, e.g. "D1 Ham", "D2 Sevk", "FBA US". */
  label: string;
  warehouseType: WarehouseType;
  isFba: boolean;
  /**
   * Column width in px: an own zone 62, an FBA zone 56 (spec §1.5). The
   * artboard's own third column ("D2 Sevk") is drawn at 56 — with generated
   * columns the type rule is the one that keeps every header readable, and a
   * 6px difference on a fixed-width scrolling grid changes nothing else.
   */
  width: number;
}

/** FBA stock is Amazon's, and it never protects an own-warehouse reorder point. */
export function isOwnWarehouseType(type: WarehouseType | null | undefined): boolean {
  return type === 'own' || type === 'transit' || type === 'third_party';
}

const OWN_COLUMN_WIDTH = 62;
const FBA_COLUMN_WIDTH = 56;
/** `coalesce(cfg.pos, 1000000)` of `v_stock_matrix`: an unconfigured zone sorts last. */
const UNCONFIGURED_POS = 1_000_000;

/**
 * Column header text. `v_stock_matrix.column_label` concatenates the warehouse
 * and the zone label, but the shipped Ayarlar › Depolar drawer asks for the
 * finished header ("Matris etiketi", placeholder "D1 Ham"), and that is what the
 * artboard prints — so a zone label wins whole, and only a zone without one
 * falls back to the warehouse. Recorded as a deviation in DECISIONS.md.
 */
function columnLabel(zone: MatrixZoneInput, warehouse: MatrixWarehouseInput): string {
  const zoneLabel = zone.short_label?.trim();
  if (zoneLabel) return zoneLabel;
  const own = warehouse.short_label?.trim() || warehouse.code?.trim() || '';
  const code = zone.code?.trim();
  // "D1 H" is still better than "D1" twice when two zones of one warehouse are
  // both in the matrix without labels of their own
  return code && code !== 'GEN' ? `${own} ${code}`.trim() : own;
}

/**
 * The visible matrix columns, in `v_stock_matrix.column_sort` order: the owner's
 * `settings.stock_matrix.columns` order first, then own warehouses before FBA,
 * then `warehouses.sort` / `code`, then `warehouse_zones.sort` / `code`. FBA
 * warehouses are created in `MARKETPLACE_ORDER` with an ascending `sort`, so
 * that clause carries the US → UK → DE order of the artboard without repeating it.
 */
export function stockMatrixColumns(input: {
  zones: MatrixZoneInput[];
  warehouses: MatrixWarehouseInput[];
  /** `settings.stock_matrix.columns`; an empty array means "zone order". */
  configured?: MatrixColumnSetting[] | null;
}): StockMatrixColumn[] {
  const byId = new Map(input.warehouses.filter((w) => w.id).map((w) => [w.id!, w]));
  const config = new Map<string, { pos: number; visible: boolean }>();
  (input.configured ?? []).forEach((c, i) => {
    if (c?.id) config.set(c.id, { pos: i, visible: c.visible !== false });
  });

  const rows = input.zones
    .filter((z) => z.id && z.warehouse_id && z.show_in_matrix !== false && z.is_active !== false && !z.deleted_at)
    .map((z) => ({ zone: z, warehouse: byId.get(z.warehouse_id!), cfg: config.get(z.id!) }))
    .filter((r) => r.warehouse && r.warehouse.is_active !== false && !r.warehouse.deleted_at && r.cfg?.visible !== false);

  return rows
    .sort((a, b) => {
      const pa = a.cfg?.pos ?? UNCONFIGURED_POS;
      const pb = b.cfg?.pos ?? UNCONFIGURED_POS;
      const fa = a.warehouse!.type === 'fba' ? 1 : 0;
      const fb = b.warehouse!.type === 'fba' ? 1 : 0;
      return (
        pa - pb ||
        fa - fb ||
        (a.warehouse!.sort ?? 0) - (b.warehouse!.sort ?? 0) ||
        (a.warehouse!.code ?? '').localeCompare(b.warehouse!.code ?? '', 'tr') ||
        (a.zone.sort ?? 0) - (b.zone.sort ?? 0) ||
        (a.zone.code ?? '').localeCompare(b.zone.code ?? '', 'tr')
      );
    })
    .map(({ zone, warehouse }) => {
      const type = warehouse!.type ?? 'own';
      const isFba = type === 'fba';
      return {
        zoneId: zone.id!,
        warehouseId: zone.warehouse_id!,
        label: columnLabel(zone, warehouse!),
        warehouseType: type,
        isFba,
        width: isFba ? FBA_COLUMN_WIDTH : OWN_COLUMN_WIDTH,
      };
    });
}

// ── rows ────────────────────────────────────────────────────────────────────
/** The `v_stock_item_summary` columns the matrix reads. */
export interface MatrixItemInput {
  item_id: string | null;
  sku?: string | null;
  name_tr?: string | null;
  type?: ItemType | null;
  status?: ItemStatus | null;
  attributes?: unknown;
  barcode?: string | null;
  stock_unit?: string | null;
  stock_unit_factor?: number | null;
  base_unit?: string | null;
  total_qty_base?: number | null;
  own_qty_base?: number | null;
  any_negative?: boolean | null;
  reorder_point?: number | null;
  stock_status?: string | null;
  is_lot_tracked?: boolean | null;
  cover_days?: number | null;
  /** All three are null for operator and guest — the view masks money by role, not by column. */
  value_base?: number | null;
  unit_cost_base?: number | null;
  standard_cost_base?: number | null;
  last_movement_at?: string | null;
  lot_count?: number | null;
}

/** The `v_stock_matrix` columns the pivot reads. */
export interface MatrixCellInput {
  item_id: string | null;
  zone_id: string | null;
  qty_base?: number | null;
}

/**
 * One cell. `present` is what separates the artboard's "–" from its "0": a zone
 * the item has no balance row in at all versus a zone whose balance is zero.
 */
export interface StockMatrixCell {
  present: boolean;
  qtyBase: Dec;
  qtyDisplay: Dec;
}

export interface StockMatrixRow {
  item: MatrixItemInput;
  itemId: string;
  /** One cell per column, same order and length as `columns`. */
  cells: (StockMatrixCell | null)[];
  totalDisplay: Dec;
  totalQtyBase: Dec;
  status: StockStatus;
  /** The row's stock value in base currency, or null when the role may not see money. */
  valueBase: Dec | null;
}

/** Base quantity → the item's stock unit; a missing or zero factor means 1:1. */
export function matrixQty(qtyBase: DecimalInput, factor: number | null | undefined): Dec {
  const f = D(factor ?? 1);
  return f.greaterThan(0) ? D(qtyBase).dividedBy(f) : D(qtyBase);
}

/** `v_stock_item_summary.stock_status` is the authority; anything unknown reads normal. */
function rowStatus(row: MatrixItemInput): StockStatus {
  const s = row.stock_status;
  return s === 'low' || s === 'critical' || s === 'negative' ? s : 'normal';
}

/**
 * The pivot: one row per item, one cell per visible column. Items with no
 * balance anywhere keep their row (an item at zero with a reorder point is
 * exactly the row "Kritik izleme" exists for) — every cell is simply absent.
 */
export function buildStockMatrixRows(input: {
  items: MatrixItemInput[];
  cells: MatrixCellInput[];
  columns: StockMatrixColumn[];
}): StockMatrixRow[] {
  const index = new Map<string, Map<string, Dec>>();
  for (const c of input.cells) {
    if (!c.item_id || !c.zone_id) continue;
    let zones = index.get(c.item_id);
    if (!zones) index.set(c.item_id, (zones = new Map()));
    // a zone can only appear once per item in the view, but summing is the safe read
    zones.set(c.zone_id, (zones.get(c.zone_id) ?? D(0)).plus(D(c.qty_base ?? 0)));
  }

  return input.items
    .filter((i) => i.item_id)
    .map((item) => {
      const zones = index.get(item.item_id!);
      const factor = item.stock_unit_factor;
      return {
        item,
        itemId: item.item_id!,
        cells: input.columns.map((col) => {
          const qtyBase = zones?.get(col.zoneId);
          return qtyBase === undefined ? null : { present: true, qtyBase, qtyDisplay: matrixQty(qtyBase, factor) };
        }),
        totalDisplay: matrixQty(item.total_qty_base ?? 0, factor),
        totalQtyBase: D(item.total_qty_base ?? 0),
        status: rowStatus(item),
        valueBase: item.value_base === null || item.value_base === undefined ? null : D(item.value_base),
      };
    });
}

// ── numbers and colours ─────────────────────────────────────────────────────
/**
 * Matrix / lot / distribution quantities: no decimals, tr-TR grouping
 * ("1.604", "24.800"), except a non-zero value between −1 and 1, which would
 * otherwise read as a flat "0" ("0,4").
 */
export function formatMatrixQty(qtyDisplay: DecimalInput): string {
  const v = D(qtyDisplay);
  const small = !v.isZero() && v.abs().lessThan(1);
  return formatNumber(v, { decimals: small ? 1 : 0, minDecimals: 0 });
}

/** The artboard's "–" (U+2013): the item has no balance row in this column. */
export const MATRIX_ABSENT = '–';
/** A cover cell with no forecast behind it (Phase 6 brings the number). */
export const MATRIX_NO_VALUE = '—';

export type StockTone = 'default' | 'warning' | 'danger';

/**
 * Cell colour, reproducing the artboard: a negative number is always danger; a
 * critical/negative item paints its non-zero own-warehouse cells danger and a
 * low one paints them warning; FBA cells are coloured only when negative.
 */
export function matrixCellTone(input: { qtyDisplay: DecimalInput; status: StockStatus; isFba: boolean }): StockTone {
  const v = D(input.qtyDisplay);
  if (v.isNegative() && !v.isZero()) return 'danger';
  if (input.isFba || v.isZero()) return 'default';
  if (input.status === 'critical' || input.status === 'negative') return 'danger';
  if (input.status === 'low') return 'warning';
  return 'default';
}

/** A total is only ever coloured by its sign (İlkeler: "Eksi işareti ve danger rengi"). */
export function matrixTotalTone(totalDisplay: DecimalInput): StockTone {
  const v = D(totalDisplay);
  return v.isNegative() && !v.isZero() ? 'danger' : 'default';
}

/**
 * Days-of-cover tone outside the heat cell (drawer tile, W-03 list):
 * ≤ 30 days danger, at or under the target warning, else plain.
 * `targetDays` is `settings.planning.target_cover_days` — **[[PLACEHOLDER]]** 45
 * as on the W-03 artboard.
 */
export const DEFAULT_TARGET_COVER_DAYS = 45;

export function coverTone(days: number | null | undefined, targetDays: number = DEFAULT_TARGET_COVER_DAYS): StockTone {
  if (days === null || days === undefined || !Number.isFinite(days)) return 'default';
  if (days <= 30) return 'danger';
  return days <= targetDays ? 'warning' : 'default';
}

// ── saved views, sorting, filtering ─────────────────────────────────────────
export const STOCK_MATRIX_VIEWS = ['all', 'critical'] as const;
export type StockMatrixView = (typeof STOCK_MATRIX_VIEWS)[number];

export interface StockMatrixPreset {
  /** Default sort while the view is active. */
  sort: StockMatrixSortKey;
  /** "Kritik izleme" groups by item type first (Hammadde → … → Sarf). */
  groupByType: boolean;
}

/**
 * The two presets the chip menu offers. Neither filters rows: the artboard lists
 * normal items while "Kritik izleme" is active, so a preset is a sort and column
 * choice (spec §2.4, open question 2). Saved views of the user's own are Phase 8.
 */
export const STOCK_MATRIX_PRESETS: Record<StockMatrixView, StockMatrixPreset> = {
  all: { sort: 'name', groupByType: false },
  critical: { sort: 'type', groupByType: true },
};

export type StockMatrixSortKey = 'name' | 'total' | 'cover' | 'type' | `zone:${string}`;

export interface StockMatrixSort {
  key: StockMatrixSortKey;
  desc: boolean;
}

/** `?sort=-total` → `{ key: 'total', desc: true }`; an unknown key falls back. */
export function parseMatrixSort(raw: string | null | undefined, fallback: StockMatrixSortKey): StockMatrixSort {
  const text = (raw ?? '').trim();
  const desc = text.startsWith('-');
  const key = (desc ? text.slice(1) : text) as StockMatrixSortKey;
  const known = key === 'name' || key === 'total' || key === 'cover' || key === 'type' || key.startsWith('zone:');
  return { key: known ? key : fallback, desc: known ? desc : false };
}

export function serialiseMatrixSort(sort: StockMatrixSort): string {
  return `${sort.desc ? '-' : ''}${sort.key}`;
}

const typeRank = (type: ItemType | null | undefined): number => {
  const i = ITEM_TYPE_ORDER.indexOf(type ?? ('service' as ItemType));
  return i < 0 ? ITEM_TYPE_ORDER.length : i;
};

const byName = (a: StockMatrixRow, b: StockMatrixRow): number =>
  (a.item.name_tr ?? '').localeCompare(b.item.name_tr ?? '', 'tr') || (a.item.sku ?? '').localeCompare(b.item.sku ?? '', 'tr');

/**
 * Sorting. A column the item has no balance row in has no number to compare, so
 * those rows keep the bottom in both directions — as do the null cover days of
 * Phase 2, which would otherwise crowd the top of a descending sort.
 */
export function sortStockMatrixRows(rows: StockMatrixRow[], sort: StockMatrixSort, columns: StockMatrixColumn[]): StockMatrixRow[] {
  const dir = sort.desc ? -1 : 1;
  const zoneIndex = sort.key.startsWith('zone:') ? columns.findIndex((c) => c.zoneId === sort.key.slice(5)) : -1;

  const compare = (a: StockMatrixRow, b: StockMatrixRow): number => {
    if (sort.key === 'type') return typeRank(a.item.type) - typeRank(b.item.type) || byName(a, b);
    if (sort.key === 'total') return a.totalDisplay.comparedTo(b.totalDisplay) * dir || byName(a, b);
    if (sort.key === 'cover') {
      const ca = a.item.cover_days ?? null;
      const cb = b.item.cover_days ?? null;
      if (ca === null || cb === null) return ca === cb ? byName(a, b) : ca === null ? 1 : -1;
      return (ca - cb) * dir || byName(a, b);
    }
    if (zoneIndex >= 0) {
      const ca = a.cells[zoneIndex] ?? null;
      const cb = b.cells[zoneIndex] ?? null;
      if (!ca || !cb) return ca === cb ? byName(a, b) : ca ? -1 : 1;
      return ca.qtyDisplay.comparedTo(cb.qtyDisplay) * dir || byName(a, b);
    }
    return byName(a, b) * dir;
  };

  return [...rows].sort(compare);
}

/**
 * Footer "Toplam stok değeri" / "Filtrelenen stok değeri": the sum of the rows
 * on screen. `masked` is true when the role may not see money at all — the
 * value column of every row is null then, which is not the same as a stock worth
 * nothing.
 */
export function stockMatrixValue(rows: StockMatrixRow[]): { valueBase: Dec; masked: boolean } {
  let total = D(0);
  let seen = false;
  for (const r of rows) {
    if (r.valueBase === null) continue;
    seen = true;
    total = total.plus(r.valueBase);
  }
  return { valueBase: total, masked: !seen && rows.length > 0 };
}

/**
 * What one column of a row is worth: quantity × the item's current moving
 * average, which is how `v_stock_on_hand` derives a balance's value. Null when
 * the role may not see costs — a masked cost must not become a zero.
 */
export function matrixCellValue(qtyBase: DecimalInput, unitCostBase: number | null | undefined): Dec | null {
  if (unitCostBase === null || unitCostBase === undefined) return null;
  return D(qtyBase).times(D(unitCostBase));
}

/**
 * CSV cell for a quantity — the export keeps display units and the Turkish
 * minus never reaches a spreadsheet (Excel would read "−46" as text).
 */
export function csvQty(qtyDisplay: DecimalInput): string {
  return formatMatrixQty(qtyDisplay).replace(MINUS, '-');
}

/**
 * FEFO: the order the lot rows are listed in and the order the W-07 lot picker
 * offers them — nearest expiry first, lots without an expiry after them (nothing
 * is shelf-life tracked yet, so in Phase 2 this reads as oldest goods first),
 * then the oldest receipt.
 */
export function sortLotsFefo<T extends { expiry_date?: string | null; received_at?: string | null; lot_no?: string | null }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      (a.expiry_date === b.expiry_date ? 0 : !a.expiry_date ? 1 : !b.expiry_date ? -1 : a.expiry_date.localeCompare(b.expiry_date)) ||
      (a.received_at ?? '').localeCompare(b.received_at ?? '') ||
      (a.lot_no ?? '').localeCompare(b.lot_no ?? '', 'tr'),
  );
}
